import React, { useState, useEffect } from 'react';
import { StyleSheet, css } from 'aphrodite';
import { motion } from 'framer-motion';
import * as Yup from 'yup';
import passwordValidator from 'password-validator';
import {
  showToast,
  validateConfirmPassword
} from '../../../utils/user/patient/helpers';
import { useDispatch, useSelector } from 'react-redux';
import { CircularProgress } from '@mui/material';
import { Eye, EyeOff } from 'react-feather';
import { patientResetPassword } from '../../../redux/middleware/user/patient/auth/forgot_password';
import { useParams, useNavigate } from 'react-router-dom';
import { APP_URLS } from '../../../../urls';

// Password validation schema
const passwordValidationSchema = new passwordValidator();
passwordValidationSchema
  .is()
  .min(8) // Minimum length of 8
  .has()
  .letters() // Must include letters
  .has()
  .digits() // Must include digits
  .has()
  .symbols() // Must include special characters
  .has()
  .not()
  .spaces(); // Should not contain spaces

// Yup validation schema for Reset Password
const validationSchema = Yup.object().shape({
  newPassword: Yup.string()
    .required('New password is required')
    .test(
      'isStrongPassword',
      'Password must include at least 8 characters, one number, and one special character',
      (value) => passwordValidationSchema.validate(value)
    ),
  confirmPassword: Yup.string().required('Confirm password is required')
});

const ResetPassword = ({
  toast,
  uid,
  token,
  hasValidResetParams,
  onSuccess
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { isLoading, successMessage, error } = useSelector(
    (state) => state.patientResetPassword || {}
  );

  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    if (error && toast) showToast(toast, 'error', error);
    if (successMessage) {
      if (toast) showToast(toast, 'success', successMessage);
      setFormData({ newPassword: '', confirmPassword: '' }); // Reset the form
      onSuccess();
    }
  }, [error, successMessage, toast, onSuccess]);

  const handleChange = async (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));

    if (name === 'confirmPassword') {
      await validateConfirmPassword(formData.newPassword, value, setErrors);
    }

    try {
      await validationSchema.validateAt(name, { [name]: value });
      setErrors((prev) => ({ ...prev, [name]: '' }));
    } catch (error) {
      setErrors((prev) => ({ ...prev, [name]: error.message }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate confirm password
    await validateConfirmPassword(
      formData.newPassword,
      formData.confirmPassword,
      setErrors
    );

    // Check for errors
    if (Object.values(errors).some((error) => error !== '')) {
      showToast(toast, 'error', 'Please fix the errors in the form.');
      return;
    }

    try {
      // Validate the form
      await validationSchema.validate(formData, { abortEarly: false });

      // Call the reset password API
      dispatch(
        patientResetPassword(uid, token, {
          new_password1: formData.newPassword,
          new_password2: formData.confirmPassword
        })
      );
    } catch (error) {
      if (error.name === 'ValidationError') {
        const validationErrors = {};
        error.inner.forEach((err) => {
          validationErrors[err.path] = err.message;
        });
        setErrors(validationErrors);
      } else {
        showToast(
          toast,
          'error',
          error.message || 'An unexpected error occurred. Please try again.'
        );
      }
    }
  };

  return (
    <div>
      {hasValidResetParams
        ? (
          <form onSubmit={handleSubmit} className={css(styles.form)}>
            <motion.div
              className={css(styles.inputGroup)}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.5 }}
            >
              <div className={css(styles.passwordInputWrapper)}>
                <input
                  type={showNewPassword ? 'text' : 'password'}
                  name='newPassword'
                  placeholder='New Password'
                  value={formData.newPassword}
                  onChange={handleChange}
                  className={css(styles.input, styles.passwordInput)}
                />
                <button
                  type='button'
                  onClick={() => setShowNewPassword((prev) => !prev)}
                  className={css(styles.passwordToggle)}
                >
                  {showNewPassword
                    ? (
                      <EyeOff className={css(styles.eyeIcon)} />
                      )
                    : (
                      <Eye className={css(styles.eyeIcon)} />
                      )}
                </button>
              </div>
              {errors.newPassword && (
                <p className={css(styles.error)}>{errors.newPassword}</p>
              )}
            </motion.div>
            <motion.div
              className={css(styles.inputGroup)}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.6 }}
            >
              <div className={css(styles.passwordInputWrapper)}>
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  name='confirmPassword'
                  placeholder='Confirm Password'
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className={css(styles.input, styles.passwordInput)}
                />
                <button
                  type='button'
                  onClick={() => setShowConfirmPassword((prev) => !prev)}
                  className={css(styles.passwordToggle)}
                >
                  {showConfirmPassword
                    ? (
                      <EyeOff className={css(styles.eyeIcon)} />
                      )
                    : (
                      <Eye className={css(styles.eyeIcon)} />
                      )}
                </button>
              </div>
              {errors.confirmPassword && (
                <p className={css(styles.error)}>{errors.confirmPassword}</p>
              )}
            </motion.div>
            <motion.button
              type='submit'
              className={css(styles.button)}
              disabled={isLoading}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.3 }}
            >
              {isLoading
                ? (
                  <CircularProgress size={24} color='inherit' />
                  )
                : (
						  'Confirm Password'
                  )}
            </motion.button>
          </form>
          )
        : (
          <motion.div
            className={css(styles.warning)}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.3 }}
          >
            Invalid or expired reset link.
          </motion.div>
          )}
    </div>
  );
};

const styles = StyleSheet.create({
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem'
  },
  inputGroup: {
    position: 'relative'
  },
  input: {
    width: '100%',
    padding: '0.75rem',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
    fontSize: '1rem',
    transition: 'border-color 0.3s',
    ':focus': {
      outline: 'none',
      borderColor: '#2783F7',
      boxShadow: '0 0 0 3px rgba(39, 131, 247, 0.1)'
    }
  },
  passwordInputWrapper: {
    position: 'relative',
    width: '100%'
  },
  passwordInput: {
    paddingRight: '40px'
  },
  passwordToggle: {
    position: 'absolute',
    right: '10px',
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#4a5568'
  },
  eyeIcon: {
    color: 'black',
    width: '1.15rem',
    height: '1.15rem'
  },
  button: {
    backgroundColor: '#2783F7',
    color: 'white',
    padding: '0.75rem',
    borderRadius: '8px',
    border: 'none',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'background-color 0.3s',
    ':hover': {
      backgroundColor: '#1c5fa8'
    },
    ':disabled': {
      backgroundColor: '#a0aec0',
      cursor: 'not-allowed'
    }
  },
  error: {
    color: '#e53e3e',
    fontSize: '0.875rem',
    marginTop: '0.25rem'
  },
  warning: {
    color: '#e53e3e',
    fontSize: '1rem',
    textAlign: 'center',
    marginTop: '1rem'
  }
});

export default ResetPassword;
